import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Asset, AssetInput, AssetStatus, Station } from '@hcc/types';
import { api } from '../lib/api';

const ROOT = ['assets'] as const;
const ITEM = (id: string) => ['assets', id] as const;
const COUNTS = ['assets', '_counts'] as const;

export interface AssetsListFilter {
  station?: Station;
  status?: AssetStatus;
  assignee?: string;
  search?: string;
  include_retired?: boolean;
}

export interface AssetCountsByStation {
  IT: AssetCounts;
  PPE: AssetCounts;
  Tools: AssetCounts;
  Fleet: AssetCounts;
  DBSHR: AssetCounts;
}
export interface AssetCounts {
  available: number;
  assigned: number;
  issued: number;
  retired: number;
  total: number;
}

export interface AssetsImportReport {
  total: number;
  inserted: number;
  skipped: number;
  /** Of the inserted rows, how many were also pre-assigned to a person. */
  assigned: number;
  /** Of the inserted rows, how many are flagged confirm-on-arrival (TUPE). */
  to_confirm: number;
  /** Existing assets whose syscode was backfilled from the sheet. */
  syscode_updated: number;
  errors: { row: number; message: string }[];
  dry_run: boolean;
}

function qs(filter: AssetsListFilter): string {
  const sp = new URLSearchParams();
  if (filter.station) sp.set('station', filter.station);
  if (filter.status) sp.set('status', filter.status);
  if (filter.assignee) sp.set('assignee', filter.assignee);
  if (filter.search) sp.set('search', filter.search);
  if (filter.include_retired) sp.set('include_retired', 'true');
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export function useAssetsList(filter: AssetsListFilter = {}) {
  return useQuery({
    queryKey: [...ROOT, filter] as const,
    queryFn: async () => {
      const r = await api<{ assets: Asset[] }>(`/api/admin/assets${qs(filter)}`);
      return r.assets;
    },
  });
}

export function useAssetsCounts() {
  return useQuery({
    queryKey: COUNTS,
    queryFn: async () => {
      const r = await api<{ counts: AssetCountsByStation }>(
        '/api/admin/assets/_counts',
      );
      return r.counts;
    },
  });
}

export function useAsset(id: string | null) {
  return useQuery({
    queryKey: id ? ITEM(id) : ['assets', 'none'],
    enabled: !!id,
    queryFn: async () => {
      const r = await api<{ asset: Asset }>(`/api/admin/assets/${id}`);
      return r.asset;
    },
  });
}

export interface AssetHistoryEvent {
  id: string;
  event_type: string;
  created_at: string;
  station: string | null;
  notes: string | null;
  item: {
    name?: string;
    quantity?: number;
    size?: string;
    serial?: string;
    registration?: string;
    notes?: string;
    asset_id?: string;
    outcome?: 'issued' | 'confirmed' | 'not_present';
    last_service_date?: string;
  } | null;
  person: { id: string; full_name: string | null };
  actor: { id: string; display_name: string | null } | null;
  /** For KitFormSubmitted rows: the signed handover checklist's submission id. */
  submission_id?: string | null;
}

export function useAssetHistory(id: string | null) {
  return useQuery({
    queryKey: id ? (['assets', id, 'history'] as const) : ['assets', 'none', 'history'],
    enabled: !!id,
    queryFn: async () => {
      const r = await api<{ asset: Asset; history: AssetHistoryEvent[] }>(
        `/api/admin/assets/${id}/history`,
      );
      return r.history;
    },
  });
}

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AssetInput) => {
      const r = await api<{ asset: Asset }>('/api/admin/assets', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      return r.asset;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOT });
      qc.invalidateQueries({ queryKey: COUNTS });
    },
  });
}

export function useUpdateAsset(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<AssetInput>) => {
      const r = await api<{ asset: Asset }>(`/api/admin/assets/${id}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      });
      return r.asset;
    },
    onSuccess: (asset) => {
      qc.setQueryData(ITEM(id), asset);
      qc.invalidateQueries({ queryKey: ROOT });
      qc.invalidateQueries({ queryKey: COUNTS });
    },
  });
}

export function useAssignAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; person_id: string }) => {
      const r = await api<{ asset: Asset }>(
        `/api/admin/assets/${vars.id}/assign`,
        {
          method: 'POST',
          body: JSON.stringify({ person_id: vars.person_id }),
        },
      );
      return r.asset;
    },
    onSuccess: (asset) => {
      qc.setQueryData(ITEM(asset.id), asset);
      qc.invalidateQueries({ queryKey: ROOT });
      qc.invalidateQueries({ queryKey: COUNTS });
    },
  });
}

export function useUnassignAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api<{ asset: Asset }>(
        `/api/admin/assets/${id}/unassign`,
        { method: 'POST' },
      );
      return r.asset;
    },
    onSuccess: (asset) => {
      qc.setQueryData(ITEM(asset.id), asset);
      qc.invalidateQueries({ queryKey: ROOT });
      qc.invalidateQueries({ queryKey: COUNTS });
    },
  });
}

/**
 * Mark an asset as issued to a person without a signature (admin record
 * correction for kit handed over outside the app). Records a KitIssued event
 * so the register stays consistent. Invalidates this asset's history too.
 */
export function useIssueAssetManually() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; person_id: string }) => {
      const r = await api<{ asset: Asset }>(
        `/api/admin/assets/${vars.id}/issue-manual`,
        {
          method: 'POST',
          body: JSON.stringify({ person_id: vars.person_id }),
        },
      );
      return r.asset;
    },
    onSuccess: (asset) => {
      qc.setQueryData(ITEM(asset.id), asset);
      qc.invalidateQueries({ queryKey: ROOT });
      qc.invalidateQueries({ queryKey: COUNTS });
      qc.invalidateQueries({ queryKey: ['assets', asset.id, 'history'] });
    },
  });
}

/**
 * Return an already-issued asset (admin correction): records a KitReturned
 * event so the asset frees up and the register stays consistent. Also
 * invalidates this asset's history so the "Returned" row shows immediately.
 */
export function useReturnAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api<{ asset: Asset }>(`/api/admin/assets/${id}/return`, {
        method: 'POST',
      });
      return r.asset;
    },
    onSuccess: (asset) => {
      qc.setQueryData(ITEM(asset.id), asset);
      qc.invalidateQueries({ queryKey: ROOT });
      qc.invalidateQueries({ queryKey: COUNTS });
      qc.invalidateQueries({ queryKey: ['assets', asset.id, 'history'] });
    },
  });
}

export function useSetAssetStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; status: AssetStatus }) => {
      const r = await api<{ asset: Asset }>(
        `/api/admin/assets/${vars.id}/status`,
        {
          method: 'POST',
          body: JSON.stringify({ status: vars.status }),
        },
      );
      return r.asset;
    },
    onSuccess: (asset) => {
      qc.setQueryData(ITEM(asset.id), asset);
      qc.invalidateQueries({ queryKey: ROOT });
      qc.invalidateQueries({ queryKey: COUNTS });
    },
  });
}

export function useDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api<void>(`/api/admin/assets/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOT });
      qc.invalidateQueries({ queryKey: COUNTS });
    },
  });
}

export function useImportAssets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { csv: string; dry_run: boolean }) => {
      return api<AssetsImportReport>('/api/admin/assets/import', {
        method: 'POST',
        body: JSON.stringify(vars),
      });
    },
    onSuccess: (report) => {
      if (!report.dry_run) {
        qc.invalidateQueries({ queryKey: ROOT });
        qc.invalidateQueries({ queryKey: COUNTS });
      }
    },
  });
}
